<template>
  <div class="w-full flex justify-center gap-2">
    <div class="flex flex-col glass-frosted rounded-8xl w-83 h-67.5 p-4 min-h-fit">
      <div class="flex justify-between">
        <Button color="normal" class="w-12 h-6" size="sm" @click="emit('cancel')">
          {{ $t('common.backButton') }}
        </Button>
        <Button
          color="glass"
          class="w-12 h-6"
          size="sm"
          :active="isCanEnabled"
          :disabled="!isCanEnabled"
          @click="handleTakeEffect"
        >
          {{ $t('keys.socd.apply') }}
        </Button>
      </div>

      <div class="flex-1 flex flex-col justify-center items-center">
        <div class="flex items-center gap-16">
          <div
            v-for="(slot, slotIndex) in socdSlots"
            :key="slot.value"
            :class="`relative flex flex-col items-center ${selectedSlot === slot.value ? 'text-text-important-default' : 'text-text-body-default'}`"
          >
            <div
              :class="`flex justify-center items-center w-14 h-14 px-0.5 text-black-white rounded-xl border border-solid bg-key cursor-pointer transition-all hover:border-blue-light-hover ${getSlotBorderClass(slot.value)}`"
              @click="selectedSlot = slot.value"
            >
              <span class="w-full text-center text-l-medium leading-4 line-clamp-2">
                {{ getBoundKeyLabel(currentSelection[slot.value]) }}
              </span>
            </div>
            <span class="text-m-medium text-black-white">{{ slot.label }}</span>
            <Icon
              v-show="currentSelection[slot.value]"
              icon="Function-Fill-Close"
              class="absolute -top-1 -right-1 w-4 h-4 text-brand-red cursor-pointer"
              @click.stop="clearSlot(slot.value)"
            />
            <Icon
              v-if="slotIndex === 0"
              icon="Function-Wireframe-HorizontalBind"
              class="absolute top-4 -right-11 w-6 h-6 text-black-white pointer-events-none"
            />
          </div>
        </div>

        <div
          :class="[
            'mt-3 h-5 flex justify-center items-center transition-opacity',
            isAbsolutePriorityMode ? 'opacity-100' : 'opacity-0 invisible pointer-events-none',
          ]"
        >
          <div class="flex items-center gap-5.25">
            <button
              type="button"
              class="w-14 flex justify-center"
              :disabled="!isPriorityInteractive"
              @click="priorityKey = 'key1'"
            >
              <Icon
                :icon="
                  isPriorityDisplaySelected('key1')
                    ? 'Switch-Fill-Selected'
                    : 'Switch-Wireframe-Unselected'
                "
                :class="[
                  'w-5 h-5 transition-colors',
                  getSelectIconClass(isPriorityInteractive, isPriorityDisplaySelected('key1')),
                ]"
              />
            </button>
            <div class="w-6" />
            <button
              type="button"
              class="w-14 flex justify-center"
              :disabled="!isPriorityInteractive"
              @click="priorityKey = 'key2'"
            >
              <Icon
                :icon="
                  isPriorityDisplaySelected('key2')
                    ? 'Switch-Fill-Selected'
                    : 'Switch-Wireframe-Unselected'
                "
                :class="[
                  'w-5 h-5 transition-colors',
                  getSelectIconClass(isPriorityInteractive, isPriorityDisplaySelected('key2')),
                ]"
              />
            </button>
          </div>
        </div>
      </div>

      <div class="flex items-center text-text-glass-default">
        <Icon icon="Function-FiII-Description" class="size-4" />
        <span class="text-s-medium">{{ $t('keys.advanced.bindHint') }}</span>
      </div>
    </div>

    <div class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4 min-h-fit">
      <div class="flex gap-2">
        <div class="basis-2/5 flex flex-col gap-2">
          <div
            v-for="item in methodList"
            :key="item.subMode"
            :class="[
              'flex-1 flex items-center gap-1 px-2 py-1.5 bg-key rounded-4xl transition-all cursor-pointer group',
              {
                'bg-white': activeMethod === item.subMode,
                'bg-key!': isPanelDisabled && activeMethod === item.subMode,
              },
              { 'pointer-events-none': isPanelDisabled },
            ]"
            @click="activeMethod = item.subMode"
          >
            <Icon
              :icon="item.icon"
              :class="[
                'w-8 h-8 transition-colors',
                `${activeMethod === item.subMode ? 'text-brand-blue2' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
                { 'text-text-disabled!': isPanelDisabled },
              ]"
            />
            <div>
              <p
                :class="[
                  'text-m-medium transition-colors',
                  `${activeMethod === item.subMode ? 'text-brand-blue2' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
                  { 'text-text-disabled!': isPanelDisabled },
                ]"
              >
                {{ item.title }}
              </p>
              <p
                :class="[
                  'text-s-medium transition-colors',
                  `${activeMethod === item.subMode ? 'text-blue-dark-hover' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
                  { 'text-text-disabled!': isPanelDisabled },
                ]"
              >
                {{ item.excerpt }}
              </p>
            </div>
          </div>
        </div>

        <div class="basis-3/5 flex flex-col">
          <div class="p-4 flex flex-col gap-1 h-20 shrink-0 bg-key rounded-4xl">
            <div class="flex items-center justify-between">
              <div class="flex items-center text-black-white">
                <Icon icon="Technology-Wireframe-QuickTrigger" class="w-6 h-6" />
                <span class="text-m-medium">{{ $t('keys.socd.quickTrigger') }}</span>
              </div>
              <Switch v-model="rtEnabled" :disabled="isPanelDisabled" />
            </div>
            <p class="text-text-glass-default text-s-medium">
              {{ $t('keys.socd.quickTriggerDescription') }}
            </p>
          </div>
          <div class="p-4 mt-2 flex-1 flex flex-col gap-1 bg-key rounded-4xl">
            <div class="flex items-center justify-between">
              <div class="flex items-center text-black-white">
                <Icon icon="Function-Wireframe-CurrentClick" class="w-6 h-6" />
                <span class="text-m-medium">{{ $t('keys.socd.executePoint') }}</span>
              </div>
              <div class="flex items-center gap-1.5">
                <span class="text-text-glass-default text-s-medium">
                  {{ $t('keys.socd.bottomTrigger') }}
                </span>
                <Switch v-model="triggerWhenBottomEnabled" size="md" :disabled="isPanelDisabled" />
              </div>
            </div>
            <p class="text-text-glass-default text-s-medium">
              {{ $t('keys.socd.executePointDescription') }}
            </p>
            <div class="flex items-start gap-3 w-full">
              <span class="text-black-white text-s-medium shrink-0">{{ MIN }}</span>

              <div class="relative flex-1 flex items-start h-16">
                <div class="relative flex-1 h-5 flex items-center">
                  <Slider
                    v-model="value"
                    :min="SLIDE_MIN"
                    :max="SLIDE_MAX"
                    :step="1"
                    :disabled="isPanelDisabled"
                    class="w-full"
                    track-class="bg-glass30"
                    range-class="bg-brand-blue1 disabled:bg-disabled"
                    thumb-class="bg-white"
                  />
                  <div
                    class="absolute top-full left-0 -translate-x-1/2 pointer-events-none text-center leading-none"
                    :style="{ left: bubbleLeft }"
                  >
                    <div
                      :class="[
                        'text-brand-blue1 text-l-medium',
                        { 'text-text-disabled!': isPanelDisabled },
                      ]"
                    >
                      {{ currentValue }}
                    </div>
                    <div
                      :class="[
                        'text-text-glass-default text-s-medium',
                        { 'text-text-disabled!': isPanelDisabled },
                      ]"
                    >
                      (~{{ mmValue }}mm)
                    </div>
                  </div>
                </div>
              </div>

              <span class="text-black-white text-s-medium shrink-0">{{ MAX }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, ref, watch } from 'vue'
  import { Button, Icon, Slider, Switch, toast } from '@rapoo/element'
  import type { KeyConfig } from '@rapoo/element'
  import type { SOCD } from '@rapoo/device-sdk'
  import { useLocaleStore } from '@/stores'
  import {
    KEYCODE_KEYWORDS_MAP,
    getLocalizedField,
    buildKeySettingFromPayload,
    isRemapRestrictedKey,
  } from '../../../utils/keyMapSettings'
  import {
    GCOME_MATRIX_TYPE,
    buildRsMtSlotsFromKeys,
    applyRsSocdPreferredSlotIndexes,
    collectRsSocdRestoreTargets,
    resolveRsSocdPairContext,
  } from '../../../utils/advancedKeyMapSettings'
  import {
    AdvancedEditingSettingSymbol,
    SelectedKeySymbol,
    KeySettingsSymbol,
    KeyLayerSymbol,
    type KeyChangePayload,
    type KeySetting,
  } from '../../../types'
  import { findSettingByTargetKey, parseNumber } from '../../../utils/keyIdentity'
  import { useAdvancedKeyCommit } from '../composables/useAdvancedKeyCommit'
  import { usePhysicalKeyPressCapture } from '../composables/usePhysicalKeyPressCapture'

  const emit = defineEmits<{
    success: []
    cancel: []
  }>()

  type SlotType = 'key1' | 'key2'

  const MIN = 0
  const MAX = 255
  const SLIDE_MIN = 0
  const SLIDE_MAX = 255
  const THUMB_SIZE = 12
  const DEFAULT_SLIDER_VALUE = 37
  const THRESHOLD_MM_MIN = 0.01
  const THRESHOLD_MM_MAX = 3.4
  const THRESHOLD_MM_RANGE = THRESHOLD_MM_MAX - THRESHOLD_MM_MIN
  const APPLY_TOAST_ID = 'advanced-socd-apply'

  const sliderToMm = (slider: number) => THRESHOLD_MM_MIN + (slider / MAX) * THRESHOLD_MM_RANGE

  const { t } = useLocaleStore()
  const { commitAdvancedKey, restoreAdvancedKey } = useAdvancedKeyCommit()

  const selectedKey = inject(SelectedKeySymbol)
  const editingSetting = inject(AdvancedEditingSettingSymbol, ref<KeySetting | null>(null))
  const keySettings = inject(KeySettingsSymbol)
  const currentLayer = inject(KeyLayerSymbol)

  const selectedSlot = ref<SlotType>('key1')
  const currentSelection = ref<Record<SlotType, KeyConfig | null>>({
    key1: null,
    key2: null,
  })
  const rtEnabled = ref(false)
  const triggerWhenBottomEnabled = ref(false)
  const activeMethod = ref(1)
  const priorityKey = ref<SlotType>('key1')
  const value = ref([DEFAULT_SLIDER_VALUE])
  const isSubmitting = ref(false)
  const suppressApplyToast = ref(false)

  const methodList = computed(
    () =>
      [
        {
          icon: 'Technology-Wireframe-RearOverlay',
          title: t('keys.socd.methods.rearCover.title'),
          excerpt: t('keys.socd.methods.rearCover.excerpt'),
          subMode: 1,
        },
        {
          icon: 'Technology-Wireframe-AbsPriorityTrigger',
          title: t('keys.socd.methods.absolutePriority.title'),
          excerpt: t('keys.socd.methods.absolutePriority.excerpt'),
          subMode: 2,
        },
        {
          icon: 'Technology-Wireframe-KeyConflict',
          title: t('keys.socd.methods.conflict.title'),
          excerpt: t('keys.socd.methods.conflict.excerpt'),
          subMode: 3,
        },
      ] as const,
  )

  const socdSlots = computed<{ label: string; value: SlotType }[]>(() => [
    { label: t('keys.socd.key1'), value: 'key1' },
    { label: t('keys.socd.key2'), value: 'key2' },
  ])

  const activeSubMode = computed(() => activeMethod.value)
  const isAbsolutePriorityMode = computed(() => activeSubMode.value === 2)
  const isPriorityInteractive = computed(() =>
    Boolean(currentSelection.value.key1 && currentSelection.value.key2),
  )
  const isPanelDisabled = computed(
    () => !currentSelection.value.key1 || !currentSelection.value.key2,
  )

  const currentValue = computed(() => value.value[0] ?? SLIDE_MIN)
  const mmValue = computed(() => sliderToMm(currentValue.value).toFixed(2))
  const triggerThreshold = computed(() => Math.round(sliderToMm(currentValue.value) * 100))

  const sliderProgress = computed(() => {
    const ratio = (currentValue.value - SLIDE_MIN) / (SLIDE_MAX - SLIDE_MIN)
    return Math.min(1, Math.max(0, ratio))
  })

  const bubbleLeft = computed(() => {
    const progress = sliderProgress.value
    const offset = (0.5 - progress) * THUMB_SIZE
    return `calc(${progress * 100}% + ${offset}px)`
  })

  const isCanEnabled = computed(() =>
    Boolean(currentSelection.value.key1 && currentSelection.value.key2 && !isSubmitting.value),
  )

  const getBoundKeyLabel = (key: KeyConfig | null) => {
    if (!key) return ''
    return getLocalizedField(key.alias || key.name)
  }

  const getSlotBorderClass = (slot: SlotType) => {
    if (selectedSlot.value === slot) return 'border-brand-blue1'
    return 'border-transparent'
  }

  const getSelectIconClass = (enabled: boolean, selected: boolean) => {
    if (selected && enabled) return 'text-brand-blue1'
    if (selected && !enabled) return 'text-text-disabled'
    return enabled ? 'text-black-white' : 'text-text-disabled'
  }

  const isPriorityDisplaySelected = (slot: SlotType) => {
    if (!isPriorityInteractive.value || !isAbsolutePriorityMode.value) return true
    return priorityKey.value === slot
  }

  const parseSliderValueByThreshold = (threshold: unknown) => {
    const parsed =
      typeof threshold === 'number' ? threshold : Number.parseInt(String(threshold ?? ''), 10)
    if (Number.isNaN(parsed)) return DEFAULT_SLIDER_VALUE
    const clamped = Math.min(THRESHOLD_MM_MAX * 100, Math.max(THRESHOLD_MM_MIN * 100, parsed))
    const slider = Math.round(((clamped / 100 - THRESHOLD_MM_MIN) / THRESHOLD_MM_RANGE) * MAX)
    return Math.min(MAX, Math.max(MIN, slider))
  }

  const toBoundKey = (keywords: string, index?: number): KeyConfig | null => {
    const config = KEYCODE_KEYWORDS_MAP.get(keywords)
    if (!config) return null
    return { ...config, key: index as number }
  }

  const restorePairFromSetting = (setting: KeySetting) => {
    const key1Index = parseNumber(setting.raw?.index)
    const key2Index = parseNumber(
      setting.raw?.relatedKeyIndex ?? setting.raw?.socd?.relatedKeyIndex,
    )
    const key2Keywords = setting.raw?.relatedKeyKeywords as string | undefined

    currentSelection.value = {
      key1: toBoundKey(setting.keywords, key1Index),
      key2: key2Keywords ? toBoundKey(key2Keywords, key2Index) : null,
    }

    const socd = setting.raw?.socd as SOCD | undefined
    rtEnabled.value = socd?.combineRT === 1
    triggerWhenBottomEnabled.value = socd?.triggerWhenBottom === 1
    activeMethod.value = socd?.subMode ?? 1
    priorityKey.value = socd?.selfPriority === 1 ? 'key1' : 'key2'
    value.value = [parseSliderValueByThreshold(socd?.triggerThreshold)]
    selectedSlot.value = currentSelection.value.key2 ? 'key1' : 'key2'
  }

  const assignToSlot = (slot: SlotType, key: KeyConfig) => {
    const other = slot === 'key1' ? currentSelection.value.key2 : currentSelection.value.key1
    if (other?.keywords && other.keywords === key.keywords) {
      toast.info(t('toast.keys.socdSameKey'))
      return
    }

    currentSelection.value[slot] = key
    if (slot === 'key1' && !currentSelection.value.key2) selectedSlot.value = 'key2'
    else if (slot === 'key2' && !currentSelection.value.key1) selectedSlot.value = 'key1'
  }

  const clearSlot = (slot: SlotType) => {
    currentSelection.value[slot] = null
    selectedSlot.value = slot
  }

  const handlePhysicalKeyPress = (key: KeyConfig) => {
    if (isRemapRestrictedKey(key.keywords)) {
      toast.info(t('toast.keys.fnNotSupported'))
      return
    }
    assignToSlot(selectedSlot.value, key)
  }

  usePhysicalKeyPressCapture({
    enabled: () => !isSubmitting.value,
    onKeyPress: handlePhysicalKeyPress,
  })

  watch(
    editingSetting,
    (setting) => {
      if (setting?.keyType === 'SOCD') {
        restorePairFromSetting(setting)
      }
    },
    { immediate: true },
  )

  watch(
    () => selectedKey?.value,
    (key) => {
      if (!key) return

      assignToSlot(selectedSlot.value, key)
    },
  )

  watch(
    editingSetting,
    () => {
      suppressApplyToast.value = false
    },
    { immediate: true },
  )

  watch(isCanEnabled, (enabled, wasEnabled) => {
    if (suppressApplyToast.value) return
    if (enabled && !wasEnabled) {
      toast.info(t('toast.keys.socdApply'), { id: APPLY_TOAST_ID })
    }
  })

  const resolveKeyIndex = (key: KeyConfig) => {
    const existing = keySettings?.value ? findSettingByTargetKey(keySettings.value, key) : undefined
    return parseNumber(existing?.raw?.index) ?? parseNumber(key.key)
  }

  const handleTakeEffect = async () => {
    const key1 = currentSelection.value.key1
    const key2 = currentSelection.value.key2
    if (!key1 || !key2) {
      toast.info(t('toast.keys.socdSelect'))
      return
    }

    const index1 = resolveKeyIndex(key1)
    const index2 = resolveKeyIndex(key2)
    if (index1 == null || index2 == null) return

    const { existingSetting, peerExistingSetting, originalPairSettings, editingPeer } =
      resolveRsSocdPairContext({
        key1,
        key2,
        keySettings: keySettings?.value,
        editingSetting: editingSetting?.value,
      })

    const { mt, peerMt } = buildRsMtSlotsFromKeys({ key1, key2 })
    const socd: SOCD = {
      combineRT: rtEnabled.value ? 1 : 0,
      relatedKeyIndex: index2,
      triggerKeyType: 0x00,
      triggerKeyCode: parseNumber(key1.code) || 0,
      triggerKeyExtCode: 0x00,
      triggerThreshold: triggerThreshold.value,
      subMode: activeSubMode.value,
      triggerWhenBottom: triggerWhenBottomEnabled.value ? 1 : 0,
      selfPriority: activeSubMode.value === 2 && priorityKey.value === 'key1' ? 1 : 0,
    }

    const payload: KeyChangePayload = {
      keywords: key1.keywords,
      keyType: 'SOCD',
      target: null,
      raw: {
        type: GCOME_MATRIX_TYPE.SOCD,
        mt,
        peerMt,
        relatedKeyIndex: index2,
        relatedKeyKeywords: key2.keywords,
        relatedKeyLabel: getBoundKeyLabel(key2),
        peerKeyIndex: index2,
        originalLabel: getBoundKeyLabel(key1),
        socd,
      },
    }

    const newSetting = buildKeySettingFromPayload({
      sourceData: payload,
      index: index1,
    })
    if (!newSetting) return

    applyRsSocdPreferredSlotIndexes(newSetting, {
      existingSetting,
      peerExistingSetting,
      editingSetting: editingSetting?.value,
      editingPeer,
    })

    let didSucceed = false
    suppressApplyToast.value = true
    toast.dismiss(APPLY_TOAST_ID)
    isSubmitting.value = true
    try {
      const committed = await commitAdvancedKey({
        setting: newSetting,
        existingSetting,
        peerExistingSetting,
        isFn: currentLayer?.value === 'fn',
      })
      if (!committed) {
        suppressApplyToast.value = false
        return
      }

      await restoreReplacedPairKeys({
        key1,
        key2,
        originalPairSettings,
        existingSetting,
        peerExistingSetting,
        reusedSlotIndexes: [newSetting.raw?.mtIndex, newSetting.raw?.peerMtIndex],
      })

      toast.dismiss('keyboard')
      emit('success')
      didSucceed = true
    } finally {
      if (!didSucceed) isSubmitting.value = false
    }
  }

  const restoreReplacedPairKeys = async ({
    key1,
    key2,
    originalPairSettings,
    existingSetting,
    peerExistingSetting,
    reusedSlotIndexes,
  }: {
    key1: KeyConfig
    key2: KeyConfig
    originalPairSettings: KeySetting[]
    existingSetting?: KeySetting
    peerExistingSetting?: KeySetting
    reusedSlotIndexes: unknown[]
  }) => {
    const reusedSlots = new Set(
      reusedSlotIndexes
        .map((index) => parseNumber(index))
        .filter((index): index is number => index != null),
    )

    const targets = collectRsSocdRestoreTargets({
      keepKeywords: [key1.keywords, key2.keywords],
      keySettings: keySettings?.value,
      originalPairSettings,
      existingSetting,
      peerExistingSetting,
      keyType: 'SOCD',
    })

    for (const leftover of targets) {
      const leftoverSlot = parseNumber(leftover.raw?.mtIndex)
      const defaultKey = toBoundKey(leftover.keywords, parseNumber(leftover.raw?.index))
      if (!defaultKey) continue

      await restoreAdvancedKey({
        setting: leftover,
        defaultKey,
        isFn: currentLayer?.value === 'fn',
        clearSlot: leftoverSlot == null || !reusedSlots.has(leftoverSlot),
      })
    }
  }
</script>

<style scoped></style>
